import { cn } from '@/lib/cn'

export type ConfidenceLevel = 'high' | 'medium' | 'low'

export interface ConfidenceBarProps {
  /** 0..1 albo 0..100 — oba zapisy są akceptowane. */
  value: number | null | undefined
  /** 'none' — sam pasek, 'percent' — „94%", 'prefixed' — „pewność 94%". */
  label?: 'none' | 'percent' | 'prefixed'
  /** Szerokość paska w px (domyślnie wypełnia kontener). */
  width?: number
  className?: string
}

/** Progi zgodne z CONFIDENCE_THRESHOLD=0.80 i kolorami z mockupów. */
export function confidenceLevel(value: number): ConfidenceLevel {
  const pct = value <= 1 ? value * 100 : value
  if (pct >= 90) return 'high'
  if (pct >= 80) return 'medium'
  return 'low'
}

const FILL: Record<ConfidenceLevel, string> = {
  high: 'bg-success',
  medium: 'bg-warning',
  low: 'bg-danger',
}

const TEXT: Record<ConfidenceLevel, string> = {
  high: 'text-success',
  medium: 'text-warning',
  low: 'text-danger',
}

/** Pasek pewności modelu — skrzynka wpływu, weryfikacja dokumentu, pulpit. */
export function ConfidenceBar({ value, label = 'percent', width, className }: ConfidenceBarProps) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <span className={cn('text-micro text-muted', className)}>—</span>
  }
  const pct = Math.max(0, Math.min(100, value <= 1 ? value * 100 : value))
  const level = confidenceLevel(pct)
  const rounded = Math.round(pct)

  return (
    <div className={cn('inline-block align-middle', className)} style={width ? { width } : undefined}>
      {label !== 'none' ? (
        <div className="mb-1 flex items-baseline gap-1 text-[12.5px] leading-none">
          {label === 'prefixed' ? <span className="text-muted">pewność</span> : null}
          <span className={cn('font-mono font-medium tabular-nums', TEXT[level])}>{rounded}%</span>
        </div>
      ) : null}
      <div
        className="h-[3px] w-full overflow-hidden rounded-sm bg-[#e6eaee]"
        role="meter"
        aria-valuenow={rounded}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Pewność rozpoznania"
      >
        <div className={cn('h-full rounded-sm', FILL[level])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
